/**
 * Shared org/event branding resolvers for purchase-flow pages.
 */

function mediaUrl(media) {
  if (!media) return null
  if (typeof media === 'string') return media
  return (
    media.url ||
    media?.formats?.large?.url ||
    media?.formats?.medium?.url ||
    media?.formats?.small?.url ||
    media?.formats?.thumbnail?.url ||
    null
  )
}

function isUsableBranding(branding) {
  return Boolean(
    branding &&
    branding.enabled !== false &&
    (branding.primaryColor ||
      branding.buttonColor ||
      mediaUrl(branding.logo) ||
      mediaUrl(branding.darkLogo))
  )
}

/**
 * Event branding wins when explicitly usable; otherwise fall back to org branding.
 */
export function getActiveBranding(event, organization = event?.organization) {
  const eventBranding = event?.branding
  const orgBranding = organization?.branding
  if (isUsableBranding(eventBranding)) return eventBranding
  if (isUsableBranding(orgBranding)) return orgBranding
  return orgBranding || eventBranding || null
}

export function resolvePrimaryColor(event, organization = event?.organization) {
  const branding = getActiveBranding(event, organization)
  if (branding?.primaryColor) return branding.primaryColor
  return organization?.primaryColor || organization?.brandColor || '#051B35'
}

export function resolveButtonColor(
  event,
  organization = event?.organization,
  fallbackAccent
) {
  const branding = getActiveBranding(event, organization)
  return (
    branding?.buttonColor ||
    branding?.primaryColor ||
    fallbackAccent ||
    resolvePrimaryColor(event, organization)
  )
}

export function resolveButtonTextColor(
  event,
  organization = event?.organization
) {
  const branding = getActiveBranding(event, organization)
  return branding?.buttonTextColor || '#FFFFFF'
}

/**
 * Artwork for the attraction marked primary. If none is marked, the
 * lowest-order attraction with artwork is used.
 */
export function resolvePrimaryAttractionImage(event) {
  const attractions = Array.isArray(event?.attractions) ? event.attractions : []
  const withArtwork = attractions
    .map((attraction) => ({
      attraction,
      url: mediaUrl(attraction?.artwork),
    }))
    .filter((item) => item.url)

  if (withArtwork.length === 0) return null

  const primary = withArtwork.find((item) => item.attraction?.primary === true)
  const chosen =
    primary ||
    [...withArtwork].sort(
      (a, b) => (a.attraction?.order ?? 0) - (b.attraction?.order ?? 0)
    )[0]

  return {
    url: chosen.url,
    name: chosen.attraction?.name || null,
  }
}

/**
 * Light-header logo preference: branding logo → org image → darkLogo.
 */
export function resolveBrandLogo(event, organization = event?.organization) {
  const branding = getActiveBranding(event, organization)
  return (
    mediaUrl(branding?.logo) ||
    mediaUrl(event?.branding?.logo) ||
    mediaUrl(organization?.branding?.logo) ||
    mediaUrl(organization?.image) ||
    mediaUrl(organization?.logo) ||
    organization?.logoUrl ||
    mediaUrl(branding?.darkLogo) ||
    null
  )
}

export function brandingCssVars(event, organization = event?.organization) {
  const accent = resolvePrimaryColor(event, organization)
  return {
    '--spp-accent': accent,
    '--spp-button': resolveButtonColor(event, organization, accent),
    '--spp-button-text': resolveButtonTextColor(event, organization),
  }
}
